import React from 'react';
import { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { DataLayout, FileUpload, MetaDrivenTable } from '../../../shared/components';
import { meetingManager } from '../api/meetings';
import { RichText } from './RichText/RichText';
import { FileUploadRef } from '../../../shared/components/FileUpload';
import { useDisplayField } from '../../../hooks/useDisplayField';

interface MeetingSectionProps {
  section: {
    title: string;
    data: string;
    type: string;
    update_key: "agenda" |
    "summary" |
    "target_plan" |
    "programme_changes" |
    "job_packs" |
    "forecast_actuals" |
    "commercial" |
    "projects";
    meeting_id: string;
  };
}

const SectionDisplay: React.FC<{ title: string; content: string; update_key: "agenda" | "summary" | "target_plan" | "programme_changes" | "job_packs" | "forecast_actuals" | "commercial" | "projects"; meeting_id: string; }> = ({ title, content, update_key, meeting_id }) => {

  const [isEditing, setIsEditing] = React.useState(false);
  const [displayContent, setDisplayContent] = React.useState(content);
  const [editedContent, setEditedContent] = React.useState(content);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      const updatePayload = [
        {
          update_key: update_key,
          new_value: editedContent
        }
      ];

      await meetingManager.update(meeting_id, updatePayload);
      setDisplayContent(editedContent);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
      console.error('Error saving section:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedContent(displayContent);
    setIsEditing(false);
    setError(null);
  };

  const isAgenda = update_key === 'agenda';

  return (
    <Box>
      <DataLayout title={title} bgVariant='filled' action={
        <>
          {isEditing && (
            <Tooltip title="Cancel">
              <IconButton
                onClick={handleCancel}
                disabled={loading}
                size="small"
              >
              </IconButton>
            </Tooltip>
          )}
          {
            !isAgenda && (
              <Tooltip title={isEditing ? "Save" : "Edit"}>
                <IconButton
                  onClick={() => {
                    if (isEditing) {
                      handleSave();
                    } else {
                      setIsEditing(true);
                    }
                  }}
                  disabled={loading}
                  size="small"
                >
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : isEditing ? (
                    <SaveIcon sx={{ fontSize: '16px' }} />
                  ) : (
                    <EditIcon sx={{ fontSize: '16px' }} />
                  )}
                </IconButton>
              </Tooltip>
            )
          }
        </>
      }>
        <Box p={2}>
          {isEditing ? (
            isAgenda ? (
              <RichText
                content={editedContent}
                onChange={setEditedContent}
                editable={isEditing}
              />
            ) : (
              <TextField
                fullWidth
                variant="outlined"
                multiline
                rows={4}
                value={editedContent}
                onChange={event => setEditedContent(event.target.value)}
                placeholder={`Enter ${update_key.toLowerCase()}...`}
                sx={{
                  '& .MuiInputBase-root': { backgroundColor: 'background.paper' },
                  '& .MuiInputBase-input': { fontSize: '14px' },
                }}
              />
            )
          ) : isAgenda ? (
            <RichText
              content={displayContent || 'No content'}
              editable={false}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{ color: '#424242', fontSize: '14px', whiteSpace: 'pre-line' }}
            >
              {displayContent || 'No agenda'}
            </Typography>
          )}
        </Box>
      </DataLayout>
    </Box>
  );
};

const MeetingSection: React.FC<MeetingSectionProps> = ({ section }) => {
  const forecast_actuals_headers = [
    { "alias_name": "ldp_abbreviation", "label": "LDP", "display_order": 1, is_show: true },
    { "alias_name": "volume", "label": "Volume", "display_order": 2, is_show: true },
    { "alias_name": "inch_3", "label": "3\"", "display_order": 3, is_show: true },
    { "alias_name": "inch_4_5", "label": "4-5\"", "display_order": 4, is_show: true },
    { "alias_name": "inch_6_7", "label": "6-7\"", "display_order": 5, is_show: true },
    { "alias_name": "inch_8", "label": "8\"", "display_order": 6, is_show: true },
    { "alias_name": "total", "label": "Total", "display_order": 7, is_show: true },
  ];
  const projects_headers = [
    { "alias_name": "project_ref", "label": "Project Ref", "display_order": 1, is_show: true },
    { "alias_name": "project_title", "label": "Project Title", "display_order": 2, is_show: true },
    { "alias_name": "description", "label": "Description", "display_order": 3, is_show: true },
  ];
  return (
    <DataLayout title={section.update_key === 'projects' ? 'Projects' : 'Forecast Actuals'} bgVariant='filled'>
      {
        (section.update_key === 'projects') ?
          <MetaDrivenTable headers={projects_headers} data={section.data as any} dataMetrics={{}} sortDisabled />
          : <MetaDrivenTable headers={forecast_actuals_headers} data={section.data || [] as any} dataMetrics={{}} sortDisabled />
      }
    </DataLayout>
  );
};

const Attendees: React.FC<{
  attendee: {
    full_name: string;
    email_id: string;
    user_id?: string | number;
    attended?: boolean | null;
  };
  internal: {
    full_name: string;
    email_id: string;
    user_id?: string | number;
    attended?: boolean | null;
  }[];
  external: {
    full_name: string;
    email_id: string;
    attended?: boolean | null;
  }[];
  meetingId: string;
  onUpdate?: () => void;
}> = ({ attendee, internal, external, meetingId, onUpdate }) => {
  const [attended, setAttended] = React.useState<boolean | null>(
    attendee.attended ?? null
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleAttendanceChange = async (attendanceStatus: boolean | null) => {
    try {
      setLoading(true);
      setError(null);

      /** INTERNAL ATTENDEES PAYLOAD */
      const internalPayload = internal.map((a) => ({
        user_id: a.user_id,
        attended:
          a.user_id === attendee.user_id
            ? attendanceStatus
            : a.attended ?? null,
      }));

      /** EXTERNAL ATTENDEES PAYLOAD */
      const externalPayload = external.map((a) => ({
        full_name: a.full_name,
        email: a.email_id,
        attended:
          a.email_id === attendee.email_id
            ? attendanceStatus
            : a.attended ?? null,
      }));

      const updatePayload = [
        {
          update_key: "attendees",
          new_value: internalPayload,
        },
        {
          update_key: "external_attendees",
          new_value: externalPayload,
        },
      ];

      await meetingManager.update(meetingId, updatePayload);

      setAttended(attendanceStatus);
      onUpdate?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update attendance");
      console.error("Error updating attendance:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1,
        px: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        minHeight: 56,
      }}
    >
      {/* LEFT SIDE */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {attendee.full_name
            .split(" ")
            .filter(Boolean)
            .map((w) => w[0])
            .join("")
            .slice(0, 2)}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography fontSize={14} fontWeight={500}>
            {attendee.user_id
              ? attendee.full_name
              : `${attendee.full_name} (External)`}
          </Typography>
          <Typography fontSize={12} color="text.secondary">
            {attendee.email_id}
          </Typography>
        </Box>
      </Box>

      {/* ERROR */}
      {error && (
        <Alert severity="error" sx={{ mx: 1, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* ACTIONS */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip title="Mark as Attended">
          <IconButton
            size="small"
            disabled={loading}
            onClick={() => handleAttendanceChange(true)}
            sx={{
              bgcolor:
                attended === true ? "rgba(76, 175, 80, 0.15)" : "transparent",
            }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <CheckCircle
                sx={{
                  color: attended === true ? "#4CAF50" : "#9E9E9E",
                }}
              />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Mark as Did Not Attend">
          <IconButton
            size="small"
            disabled={loading}
            onClick={() => handleAttendanceChange(false)}
            sx={{
              bgcolor:
                attended === false ? "rgba(244, 67, 54, 0.15)" : "transparent",
            }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <Cancel
                sx={{
                  color: attended === false ? "#F44336" : "#9E9E9E",
                }}
              />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};


const MeetingDetailsComponent: React.FC<{ meetingId: string, meetingDetails: any, isLoading: boolean, isError: boolean, error: any, refetch: () => void }> = ({ meetingId, meetingDetails, isLoading, isError, error, refetch }) => {

  const fileUploadRef = React.useRef<FileUploadRef>(null);
  const [idEditFiles, setIdEditFiles] = React.useState<boolean>(false);

  const { getFilterOptions } = useDisplayField([]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          gap: 2,
        }}
      >
        <Typography variant="subtitle2" color="error">
          {error?.message || "Error loading data."}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        boxSizing: 'border-box',
        pr: 2
      }}
    >

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='subtitle1' color='text.primary'>
              {meetingDetails?.category_display_text || 'N/A'}
            </Typography>
            <Typography variant="caption" color='text.secondary'>{meetingDetails?.title}</Typography>
          </Box>
        </Box>
        {
          meetingDetails?.status === 'Completed' ? (
            <Chip
              variant="outlined"
              color="success"
              label={'Completed'}
            />
          ) : meetingDetails?.status === 'Overdue' ? (
            <Chip
              variant="outlined"
              label={'Overdue'}
              color="error"
            />
          ) : (
            <Chip
              variant="outlined"
              label={meetingDetails?.status}
              color="primary"
            />
          )
        }
      </Box>

      <Box>
        <DataLayout title='Attendees' bgVariant='filled'>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
            {meetingDetails?.attendees_detail?.map((attendee: any) => (
              <Attendees
                key={`internal-${attendee.user_id}`}
                attendee={attendee}
                internal={meetingDetails.attendees_detail}
                external={meetingDetails.external_attendees_detail}
                meetingId={meetingId}
                onUpdate={refetch}
              />
            ))}
            {meetingDetails?.external_attendees_detail?.map((attendee: any) => (
              <Attendees
                key={`external-${attendee.email_id}`}
                attendee={attendee}
                internal={meetingDetails.attendees_detail}
                external={meetingDetails.external_attendees_detail}
                meetingId={meetingId}
                onUpdate={refetch}
              />
            ))}
          </Box>
        </DataLayout>
      </Box>

      {
        meetingDetails?.sections.sort((a: { display_order: number; }, b: { display_order: number; }) => a.display_order - b.display_order).map((sectionData: { update_key: any; title: string; data: string; type: string; meeting_id: string; meeting_template: string; }) => {
          if (sectionData.type === 'TEXT') {
            return (
              <SectionDisplay
                title={sectionData.title}
                content={sectionData.update_key == 'agenda' ? getFilterOptions('MEETING_TEMPLATE').find((t) => t.key === meetingDetails?.template_name)?.helper_text : sectionData.data}
                update_key={sectionData.update_key as "agenda" | "summary" | "target_plan" | "programme_changes" | "job_packs" | "forecast_actuals" | "commercial" | "projects"}
                meeting_id={meetingId as string}
              />
            );
          } else if (sectionData.type === 'TABLE') {
            return (
              <Box>
                <MeetingSection
                  key={sectionData.update_key}
                  section={sectionData}
                />
              </Box>
            )
          }
          return null;
        })
      }

      <Box>
        <DataLayout title='Files' action={
          <IconButton
            size="small"
            onClick={() => { setIdEditFiles(!idEditFiles) }}
            sx={{ mr: 1 }}
          >
            {idEditFiles ? <Cancel /> : <EditIcon />}
          </IconButton>
        }>
          <Box px={2} py={2} display={'flex'} flexDirection={'column'} gap={2}>
            <FileUpload
              type={'meeting_manager'}
              onUploadComplete={() => {
                setIdEditFiles(false)
              }}
              typeId={meetingId as string}
              ref={fileUploadRef}
              isDisableDelete={!idEditFiles}
              isDisableEdit={!idEditFiles}
              key={idEditFiles ? 'uploading' : 'idle'}
            />
            {
              idEditFiles && (
                <Button
                  onClick={() => {
                    if (fileUploadRef.current && meetingId) {
                      fileUploadRef.current.setId(meetingId);
                      setTimeout(() => {
                        fileUploadRef.current?.uploadAllFiles();
                      }, 0);
                    };
                  }} sx={{ mt: 2, ml: 'auto' }} disabled={!idEditFiles}
                >
                  Upload files
                </Button>
              )
            }
          </Box>
        </DataLayout>
      </Box>

    </Box>
  );

};

export default MeetingDetailsComponent;